import React from 'react';
import {
  SearchResult,
  BoundingBox,
  BoxRender,
  getSearchTokens,
  SearchIndex,
  HighlightIndex,
} from './ocr';
import _ from 'lodash';
import { TextFragment, TextFragmentType } from '@scale/shared/types/ocr';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';

interface AttachmentState {
  index: number;
  select(index: number): void;
  boxes: BoundingBox[];
}

interface AutoCompleteState {
  search(input: string): SearchResult[];
  focus(result: SearchResult): void;
  blur(): void;
}

interface HighlightState {
  focus(input: string): void;
  blur(): void;
}

export interface AnnotationLookupResult {
  annotation: TextCollectionAnnotation;
  field: TextCollectionField;
}

interface AnnotationLookupState {
  // Only works for top-level fields
  lookupAnnotation(fieldId: string): AnnotationLookupResult | undefined;
}

interface DocumentState {
  attachment: AttachmentState;
  autoComplete: AutoCompleteState;
  higlight: HighlightState;
  annotationLookup: AnnotationLookupState;
  zIndex: number;
}

// OCR features go in React context because deeply nested components talk to each other
// and the features are not strictly required for completing the task at hand.
const DocumentContext = React.createContext<DocumentState | undefined>(undefined);

interface DocumentProps {
  attachments: number;
  keywords?: string[];
  ocrTextFragments?: TextFragment[];
  zIndex?: number;
  annotationsRef?: React.MutableRefObject<Record<string, TextCollectionAnnotation>>;
  fields: TextCollectionField[];
  children?: React.ReactNode;
}

export const DocumentProvider: React.FC<DocumentProps> = props => {
  const minConfidence = 50;
  const minTextLength = 8;
  const shortTokenLength = 3;

  // Force zIndex changes to update context listeners.
  const [zIndex, setZIndex] = React.useState(-1);
  React.useEffect(() => {
    if (props.zIndex) {
      setZIndex(props.zIndex);
    }
  }, [props.zIndex]);

  // Keep track of which attachment is currently selected.
  const [index, setIndex] = React.useState<number>(0);

  // Temporarily highlight bounding boxes that have focus in auto complete dropdown.
  const [focusBoxes, setFocusBoxes] = React.useState<BoundingBox[]>([]);

  // Temporarily highlight bounding boxes that have focus in review mode.
  const [reviewBoxes, setReviewBoxes] = React.useState<BoundingBox[]>([]);

  // Store keyword bounding boxes per attachment.
  const keywordBoxTable = React.useMemo(() => {
    return _.range(0, props.attachments).map(attachmentIndex => {
      const fragments = _.defaultTo(props.ocrTextFragments, []);
      const keywords = _.defaultTo(props.keywords, []);
      return buildKeywordBoxes(fragments, keywords, {
        attachmentIndex,
        minConfidence,
      });
    });
  }, [props.attachments, props.keywords, props.ocrTextFragments]);

  // Store text fragments in different search indexes per attachment.
  const searchIndexes = React.useMemo(() => {
    return _.range(0, props.attachments).map(attachmentIndex => {
      const fragments = _.defaultTo(props.ocrTextFragments, []);
      return buildSearchIndex(fragments, {
        attachmentIndex,
        minTextLength,
        minConfidence,
        shortTokenLength,
      });
    });
  }, [props.attachments, props.ocrTextFragments]);

  const highlightIndexes = React.useMemo(() => {
    return _.range(0, props.attachments).map(attachmentIndex => {
      const fragments = _.defaultTo(props.ocrTextFragments, []);
      return buildHighlightIndex(fragments, {
        attachmentIndex,
      });
    });
  }, [props.attachments, props.ocrTextFragments]);

  // Select attachment in carousel.
  const select = React.useCallback((index: number) => {
    setIndex(index);
    setFocusBoxes([]);
    setReviewBoxes([]);
  }, []);

  // Focus or mouseover for auto complete dropdown results.
  const autoCompleteFocus = React.useCallback((result: SearchResult): void => {
    if (result.boundingBox) {
      setFocusBoxes([result.boundingBox]);
    } else {
      setFocusBoxes([]);
    }
  }, []);

  // Blur or mouseleave in the auto complete dropdown.
  const autoCompleteBlur = React.useCallback(() => {
    setFocusBoxes([]);
  }, []);

  // Search for text in the document starting with the provided input text.
  const autoCompleteSearch = React.useCallback(
    (input: string) => {
      const searchIndex = searchIndexes[index];
      if (!searchIndex) {
        return [];
      }
      return searchIndex.search(input);
    },
    [searchIndexes, index],
  );

  const autoCompleteState = React.useMemo(() => {
    const state: AutoCompleteState = {
      search: autoCompleteSearch,
      focus: autoCompleteFocus,
      blur: autoCompleteBlur,
    };
    return state;
  }, [autoCompleteSearch, autoCompleteFocus, autoCompleteBlur]);

  // Highlight most likely OCR word boxes for input text.
  const highlightFocus = React.useCallback(
    (input: string): void => {
      const highlightIndex = highlightIndexes[index];
      if (!highlightIndex) {
        return;
      }
      const fragments = highlightIndex.match(input);
      const bboxes: BoundingBox[] = [];
      for (const fragment of fragments) {
        const bbox = toBoundingBox(fragment);
        if (bbox) {
          bboxes.push(bbox);
        }
      }
      setReviewBoxes(bboxes);
    },
    [highlightIndexes, index],
  );

  const highlightBlur = React.useCallback(() => {
    setReviewBoxes([]);
  }, []);

  const highlightState = React.useMemo(() => {
    const state: HighlightState = {
      focus: highlightFocus,
      blur: highlightBlur,
    };
    return state;
  }, [highlightFocus, highlightBlur]);

  const boxes = React.useMemo(() => {
    const keywordBoxes = _.defaultTo(keywordBoxTable[index], []);
    return [...keywordBoxes, ...focusBoxes, ...reviewBoxes];
  }, [index, keywordBoxTable, focusBoxes, reviewBoxes]);

  const attachmentState = React.useMemo(() => {
    const state: AttachmentState = {
      index,
      select,
      boxes,
    };
    return state;
  }, [index, select, boxes]);

  const lookupAnnotation = React.useCallback(
    (fieldId: string) => {
      const annotation = props.annotationsRef?.current?.[fieldId];
      const field = _.find(props.fields, field => field.field_id === fieldId);
      if (annotation && field) {
        return { annotation, field };
      }
      return undefined;
    },
    [props.annotationsRef, props.fields],
  );

  const annotationLookup: AnnotationLookupState = React.useMemo(
    () => ({
      lookupAnnotation,
    }),
    [lookupAnnotation],
  );

  const documentState = React.useMemo(() => {
    const state: DocumentState = {
      autoComplete: autoCompleteState,
      attachment: attachmentState,
      higlight: highlightState,
      annotationLookup,
      zIndex,
    };
    return state;
  }, [autoCompleteState, attachmentState, highlightState, zIndex, annotationLookup]);

  return (
    <DocumentContext.Provider value={documentState}>{props.children}</DocumentContext.Provider>
  );
};

// React hook for accessing state and handlers related to the selected attachment.
export function useAttachmentState(): AttachmentState {
  const context = React.useContext(DocumentContext);
  if (!context) {
    throw new Error('useAttachmentState must be called within a DocumentProvider');
  }
  return context.attachment;
}

// React hook for accessing handlers related to OCR auto complete.
export function useAutoComplete(): AutoCompleteState {
  const context = React.useContext(DocumentContext);
  if (!context) {
    throw new Error('useAutoComplete must be called within a DocumentProvider');
  }
  return context.autoComplete;
}

export function useHighlight(): HighlightState {
  const context = React.useContext(DocumentContext);
  if (!context) {
    throw new Error('useHighlight must be called within a DocumentProvider');
  }
  return context.higlight;
}

export function useAttachmentZIndexStyle(elevation: number = 1): React.CSSProperties {
  const context = React.useContext(DocumentContext);
  if (!context) {
    throw new Error('useHighlight must be called within a DocumentProvider');
  }
  const zIndex = context.zIndex;
  if (zIndex > 0) {
    return { zIndex: zIndex + elevation };
  }
  return {};
}

export function useAnnotationLookup(): AnnotationLookupState {
  const context = React.useContext(DocumentContext);
  if (!context) {
    throw new Error('useAnnotationLookup must be called within a DocumentProvider');
  }
  return context.annotationLookup;
}

interface TextFragmentSettings {
  attachmentIndex: number;
  minConfidence?: number;
  minTextLength?: number;
  shortTokenLength?: number;
}

function buildSearchIndex(fragments: TextFragment[], settings: TextFragmentSettings): SearchIndex {
  const searchIndex = new SearchIndex({
    shortTokenLength: _.defaultTo(settings.shortTokenLength, 0),
  });
  for (const fragment of fragments) {
    const isRelevant =
      fragment.attachmentIndex === settings.attachmentIndex &&
      fragment.confidence > _.defaultTo(settings.minConfidence, 0) &&
      fragment.text.length > _.defaultTo(settings.minTextLength, 0);
    if (!isRelevant) {
      continue;
    }
    searchIndex.add(fragment);
  }
  return searchIndex;
}

function buildKeywordBoxes(
  fragments: TextFragment[],
  keywords: string[],
  settings: TextFragmentSettings,
): BoundingBox[] {
  const bboxes: BoundingBox[] = [];
  for (const fragment of fragments) {
    const tokens = getSearchTokens(fragment.text);
    const isKeyword =
      fragment.attachmentIndex === settings.attachmentIndex &&
      fragment.confidence > _.defaultTo(settings.minConfidence, 0) &&
      tokens.length === 1 &&
      keywords.includes(tokens[0].value);
    if (!isKeyword) {
      continue;
    }
    const bbox = toBoundingBox(fragment);
    if (bbox) {
      bboxes.push(bbox);
    }
  }
  return bboxes;
}

function buildHighlightIndex(
  fragments: TextFragment[],
  settings: TextFragmentSettings,
): HighlightIndex {
  const index = new HighlightIndex();
  for (const fragment of fragments) {
    const isRelevant =
      fragment.attachmentIndex === settings.attachmentIndex &&
      fragment.fragmentType === TextFragmentType.Word &&
      !!fragment.boundingBox;
    if (isRelevant) {
      index.add(fragment);
    }
  }
  return index;
}

function toBoundingBox(fragment: TextFragment): BoundingBox | undefined {
  if (!fragment.boundingBox) {
    return;
  }
  const bbox: BoundingBox = {
    render: BoxRender.Highlight,
    top: fragment.boundingBox.Top,
    left: fragment.boundingBox.Left,
    width: fragment.boundingBox.Width,
    height: fragment.boundingBox.Height,
  };
  return bbox;
}
